import { useRef, useState } from 'react'

import { useParams } from 'react-router'

import {
  TemplateEntityType,
  type Space,
  type Media,
} from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { MediaPicker } from '@tribeplatform/react-components/MediaPicker'
import { categoriesFactory } from '@tribeplatform/react-components/Templates'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useCreateTemplate,
  useSyncTemplate,
  useTemplate,
  useUpdateTemplate,
} from '@tribeplatform/react-sdk/hooks'
import { SlateRenderer } from '@tribeplatform/react-slate-kit/components'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Container } from '@tribeplatform/react-ui-kit/Layout'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const CreateSpaceTemplateSidebar = () => {
  const { $t } = useI18n()
  const [selectedBanner, setSelectedBanner] = useState<Media>(null)
  const categories = useStaticIntl(categoriesFactory)
  const spacePreviewRef = useRef<HTMLDivElement>(null)
  const [selectedSpace, setSelectedSpace] = useState<Space>(null)
  const { templateId } = useParams<{ templateId: string }>()
  const { replace, push } = useRouter()

  const isNew = !templateId

  const { data: template } = useTemplate({
    variables: {
      id: templateId,
    },
    fields: {
      banner: {
        onEmoji: 'all',
        onImage: 'all',
        onGlyph: 'all',
      },
      entity: {
        onSpace: {
          image: {
            onEmoji: 'all',
            onImage: 'all',
            onGlyph: 'all',
          },
          slate: 'all',
        },
      },
    },
    useQueryOptions: {
      enabled: !!templateId,
    },
  })

  const templateSpace = template?.entity as Space
  const space = templateSpace ?? selectedSpace

  const { mutateAsync: createTemplate, isLoading: isCreating } =
    useCreateTemplate()
  const { mutateAsync: updateTemplate, isLoading: isUpdating } =
    useUpdateTemplate()
  const { mutateAsync: syncTemplate, isLoading: isSyncing } = useSyncTemplate()

  const onSubmit = async ({
    name,
    description,
    image,
    entityProperties,
    space,
    categories,
  }) => {
    const input = {
      name,
      description,
      bannerId: image?.id,
      entityProperties,
      categories,
    }

    if (isNew) {
      try {
        await createTemplate({
          input: {
            ...input,
            previewHtml: spacePreviewRef.current?.innerHTML,
            entityId: space?.id,
            entityType: TemplateEntityType.Space,
            bannerId: selectedBanner?.id,
          },
        })

        toast({
          title: $t({
            id: 'Space.Template.Created',
            defaultMessage: '{SPACE_CC} template created',
          }),
          description: $t({
            id: 'Space.Template.Created.Description',
            defaultMessage: 'Your new {SPACE} template is ready to be used.',
          }),
          status: 'success',
        })
        push(`/manage/customizer/templates`)
      } catch (error) {
        toast({
          title: $t({
            id: 'Space.Template.Created.Error',
            defaultMessage: 'Error creating {SPACE_CC} template',
          }),
          description: $t({
            id: 'Space.Template.Created.Error.Description',
            defaultMessage: 'There was an error creating your template.',
          }),
          status: 'error',
        })
      }
      return
    }

    try {
      await syncTemplate({
        id: template?.id,
      })
      await updateTemplate({
        id: template?.id,
        input: {
          ...input,
          previewHtml: spacePreviewRef.current?.innerHTML,
          bannerId: selectedBanner?.id,
        },
      })
      toast({
        title: $t({
          id: 'Space.Template.Updated',
          defaultMessage: '{SPACE_CC} template updated',
        }),
        description: $t({
          id: 'Space.Template.Updated.Description',
          defaultMessage: '{SPACE_CC} template was updated successfully.',
        }),
        status: 'success',
      })
      push(`/manage/customizer/templates`)
    } catch (error) {
      toast({
        title: $t({
          id: 'Space.Template.Updated.Error',
          defaultMessage: 'Error updating {SPACE_CC} template',
        }),
        description: $t({
          id: 'Space.Template.Updated.Error.Description',
          defaultMessage: 'There was an error updating your template.',
        }),
        status: 'error',
      })
    }
  }

  if (!isNew && !template) {
    return null
  }

  return (
    <Form onSubmit={onSubmit} defaultValues={template || {}}>
      <Container size="full" spacing="md">
        <LoadingOverlay isLoading={!isNew} pulse={false}>
          <Form.SpacePicker
            name="space"
            label={$t({
              id: 'Space.Template.Space',
              defaultMessage: 'Space',
            })}
            fields={{
              slate: 'all',
            }}
            value={space}
            onChange={space => {
              setSelectedSpace(space)
              replace(space?.id ? `?spaceId=${space?.id}` : `?`)
            }}
            placeholder={$t({
              id: 'Space.Template.SpacePlaceholder',
              defaultMessage: 'Select a space',
            })}
            validation={
              isNew
                ? {
                    required: $t({
                      id: 'Space.Template.SpaceRequired',
                      defaultMessage: 'Space is required',
                    }),
                  }
                : undefined
            }
            required={isNew}
          />
        </LoadingOverlay>
        <Form.Input
          name="name"
          label={$t({
            id: 'Generics.Name.Noun',
            defaultMessage: 'Name',
          })}
          required
          validation={{
            required: $t({
              id: 'Space.Template.NameRequired',
              defaultMessage: 'Name is required',
            }),
          }}
        />
        <Form.Textarea
          name="description"
          label={$t({
            defaultMessage: 'Description',
            id: 'Generics.Description',
          })}
        />
        <Form.Multiselect
          name="categories"
          label={$t({
            defaultMessage: 'Categories',
            id: 'Generics.Categories',
          })}
          options={categories.map(c => c.value)}
          labels={categories.reduce(
            (acc, c) => ({ ...acc, [c.value]: c.text }),
            {},
          )}
        />
        <MediaPicker
          value={selectedBanner ?? template?.banner}
          onChange={banner => setSelectedBanner(banner)}
        />
        <Form.Actions>
          <Button
            type="submit"
            size="lg"
            loading={isCreating || isUpdating || isSyncing}
            className="mt-4"
            fullWidth
          >
            {isNew ? (
              <T
                id="Space.Actions.CreateTemplate"
                defaultMessage="Create template"
              />
            ) : (
              <T
                id="Space.Template.Actions.SyncAndUpdate"
                defaultMessage="Sync and update"
              />
            )}
          </Button>
        </Form.Actions>
        <div className="hidden">
          <div ref={spacePreviewRef}>
            <SlateRenderer
              slate={space?.slate}
              context={{
                spaceId: space?.id,
                path: space?.relativeUrl,
                urlParams: {},
              }}
            />
          </div>
        </div>
      </Container>
    </Form>
  )
}
